import { useRef } from 'react'
import type { LiveEventAdmin } from 'api/generated'
import { Button } from 'components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card'
import { liveEventQrUrl } from 'helpers/live-events'
import { Download } from 'lucide-react'
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react'

/** Pixel of the downloadable PNG: enough for an A4/A3 print. */
const PRINT_SIZE = 2048
const QR_PROPS = {
  level: 'M',
  marginSize: 4,
  bgColor: '#ffffff',
  fgColor: '#0b0d0e',
} as const

const download = (href: string, filename: string) => {
  const link = document.createElement('a')
  link.href = href
  link.download = filename
  link.click()
}

/**
 * The official QR, same URL the big screen shows. Dark on white on purpose:
 * a low-contrast QR doesn't scan from a distance. Downloads carry no logo so
 * they always scan and can be edited by the brand's designer.
 */
const LiveEventQrCard = ({ event }: { event: LiveEventAdmin }) => {
  const url = liveEventQrUrl(event)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const filename = `qr-${event.slug}`

  const downloadPng = () => {
    const canvas = canvasRef.current
    if (canvas) download(canvas.toDataURL('image/png'), `${filename}.png`)
  }

  const downloadSvg = () => {
    const svg = svgRef.current
    if (!svg) return
    const blob = new Blob([new XMLSerializer().serializeToString(svg)], {
      type: 'image/svg+xml',
    })
    const href = URL.createObjectURL(blob)
    download(href, `${filename}.svg`)
    URL.revokeObjectURL(href)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>QR ufficiale</CardTitle>
      </CardHeader>
      <CardContent className='flex flex-wrap items-center gap-6'>
        <QRCodeSVG
          ref={svgRef}
          value={url}
          size={220}
          title={`QR ${event.name}`}
          {...QR_PROPS}
        />
        {/* Hidden high-resolution copy used only for the PNG download */}
        <QRCodeCanvas
          ref={canvasRef}
          value={url}
          size={PRINT_SIZE}
          className='hidden'
          {...QR_PROPS}
        />
        <div className='flex flex-col gap-3'>
          <p className='max-w-sm text-sm text-muted-foreground'>
            Lo stesso QR compare sul maxischermo. Cambiare slug o chiave QR lo
            rende diverso da quelli già stampati.
          </p>
          <div className='flex gap-2'>
            <Button variant='outline' onClick={downloadPng}>
              <Download className='mr-1 h-4 w-4' /> PNG (stampa)
            </Button>
            <Button variant='outline' onClick={downloadSvg}>
              <Download className='mr-1 h-4 w-4' /> SVG
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default LiveEventQrCard
